/**
 * Configuração do fluxo de aprovação/publicação por informe de Relatórios.
 *
 * COMO PLUGAR UM INFORME (ex.: matriz, se um dia entrar no fluxo) — checklist:
 *  1. Aqui: 1 entrada em `RELATORIO_APROVACAO_CONFIG` (informe, titulo, base, `secoes`
 *     na ordem das abas, `variante` {campo 'cenario'|'modo', rotulo, labels},
 *     `selectDataAnalisada`/`selectVariante`/`setDataAnalisada` do slice do informe e
 *     `habilitado` lazy com `Boolean(...)` — nunca leia flag em import-time).
 *  2. Fixture `src/mock/relatorios/<informe>/aprovacao.json` (envelope Ampere, 22/05/2026,
 *     em_revisao) + 1 linha em `FIXTURES_APROVACAO` (`mock/api/relatoriosHandler.ts`).
 *     O teste `mock/relatorios/aprovacao.contract.test.ts` FALHA se houver config sem fixture.
 *  3. `mock-server/server.js`: 1 entrada em `APROVACAO_INFORMES` (slugs, campo, seed).
 *  4. View do informe: `<RelatorioSecoesView informe="<informe>" />` (1 linha); as abas vêm
 *     da config (não mantenha array de seções na view).
 *  5. ControlPanel do informe: `useCalendarioAprovacao(config, dataAplicada, varianteAplicada)`
 *     -> `<DatePicker {...datePickerProps} />` + `{mostraLegenda && <CalendarioLegenda isAdmin />}`
 *     + `resync` no efeito de D8 (deps primitivas ano/mes/dia). Opções de variante vêm de
 *     `config.variante.labels`.
 *  6. Rota `aprovacao` (`<PainelAprovacao />` de `components/Relatorios/shared/Aprovacao`)
 *     antes do `*` do informe em `RelatoriosRouter`.
 *  7. Testes: view/ControlPanel com Provider + `baseApi.middleware` e mock de
 *     `constants/featureFlags` com as chaves `dessemAprovacao` e `relatoriosAprovacao`.
 */
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';

import { rotuloDataDoInforme } from 'helper/Relatorios/Dessem';
import {
  selectDessemCenario,
  selectDessemDataAnalisada,
  setDessemDataAnalisada,
} from 'redux/reducers/relatoriosDessemReducer';
import {
  selectDecompDataAnalisada,
  selectDecompModo,
  setDataAnalisada as setDecompDataAnalisada,
} from 'redux/reducers/relatoriosDecompReducer';
import {
  selectIntersemanalCenario,
  selectIntersemanalDataAnalisada,
  setDataAnalisada as setIntersemanalDataAnalisada,
} from 'redux/reducers/relatoriosIntersemanalReducer';
import {
  selectSemanalCenario,
  selectSemanalDataAnalisada,
  setDataAnalisada as setSemanalDataAnalisada,
} from 'redux/reducers/relatoriosSemanalReducer';
import {
  selectMensalCenario,
  selectMensalDataAnalisada,
  setDataAnalisada as setMensalDataAnalisada,
} from 'redux/reducers/relatoriosMensalReducer';
import featureFlags from 'constants/featureFlags';
import { RelatorioInforme } from 'types/Relatorios/Aprovacao';

export interface RelatorioAprovacaoSecao { slug: string; label: string; }

export interface RelatorioAprovacaoVariante {
  /** Nome do parâmetro de query/body que carrega a variante. */
  campo: 'cenario' | 'modo';
  /** Texto usado nas frases ("cenário Early", "modo Preliminar"). */
  rotulo: string;
  /** valor -> rótulo exibido (ordem das chaves = ordem das opções no ControlPanel). */
  labels: Record<string, string>;
}

export interface DataAnalisadaAprovacao { ano: number; mes: number; dia: number; }

export interface RelatorioAprovacaoConfig {
  informe: RelatorioInforme;
  /** Ex.: "Informe DESSEM". */
  titulo: string;
  /** Ex.: "/relatorios/dessem". */
  base: string;
  secoes: RelatorioAprovacaoSecao[];
  variante: RelatorioAprovacaoVariante;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectDataAnalisada: (state: any) => DataAnalisadaAprovacao;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectVariante: (state: any) => string;
  setDataAnalisada: ActionCreatorWithPayload<DataAnalisadaAprovacao>;
  /** Lazy: lido a cada uso para respeitar mocks de flag nos testes. */
  habilitado: () => boolean;
  /**
   * Texto da data do informe nas frases ("Informe de <x> publicado…"). Padrão: `dd/MM/yyyy`. O
   * DESSEM acrescenta o dia previsto: "13/10/2026 (previsão para 14/10)" (Planejamento,
   * 13/10/2026).
   */
  rotuloDaData?: (dataRodadaIso: string) => string;
}

export const DESSEM_CENARIO_LABEL: Record<string, string> = {
  early: 'Early',
  preliminar: 'Preliminar',
  pos_ons: 'POS_ONS',
};

export const DECOMP_MODO_LABEL: Record<string, string> = {
  preliminar: 'Preliminar',
  diario: 'Diário',
  resultados: 'Resultados',
};

type ConfigPorInforme = Partial<Record<RelatorioInforme, RelatorioAprovacaoConfig>>;

export const RELATORIO_APROVACAO_CONFIG: ConfigPorInforme = {
  dessem: {
    informe: 'dessem',
    titulo: 'Informe DESSEM',
    base: '/relatorios/dessem',
    secoes: [
      { slug: 'visao-geral', label: 'Visão Geral' },
      { slug: 'carga', label: 'Carga' },
      { slug: 'carga-liquida', label: 'Carga Líquida' },
      { slug: 'geracao-renovavel', label: 'Geração Renovável' },
      { slug: 'resultados', label: 'Resultados' },
      { slug: 'premissas-gerais', label: 'Premissas Gerais' },
    ],
    variante: { campo: 'cenario', rotulo: 'cenário', labels: DESSEM_CENARIO_LABEL },
    selectDataAnalisada: selectDessemDataAnalisada,
    selectVariante: selectDessemCenario,
    setDataAnalisada: setDessemDataAnalisada,
    habilitado: () => Boolean(featureFlags.dessemAprovacao || featureFlags.relatoriosAprovacao),
    rotuloDaData: rotuloDataDoInforme,
  },
  // O cenário (texto livre) do DECOMP fica fora da chave da aprovação (D2): a variante é o modo.
  decomp: {
    informe: 'decomp',
    titulo: 'Informe DECOMP',
    base: '/relatorios/decomp',
    secoes: [
      { slug: 'visao-geral', label: 'Visão Geral' },
      { slug: 'chuva-observada', label: 'Chuva Observada' },
      { slug: 'chuva-prevista', label: 'Chuva Prevista' },
      { slug: 'carga', label: 'Carga' },
      { slug: 'geracao-renovavel-unsi', label: 'Geração Renovável e UNSI' },
      { slug: 'resultados', label: 'Resultados' },
      { slug: 'empilhamento-custos', label: 'Empilhamento de Custos' },
      { slug: 'premissas-gerais', label: 'Premissas Gerais' },
    ],
    variante: { campo: 'modo', rotulo: 'modo', labels: DECOMP_MODO_LABEL },
    selectDataAnalisada: selectDecompDataAnalisada,
    selectVariante: selectDecompModo,
    setDataAnalisada: setDecompDataAnalisada,
    habilitado: () => Boolean(featureFlags.relatoriosAprovacao),
  },
  intersemanal: {
    informe: 'intersemanal',
    titulo: 'Informe Intersemanal',
    base: '/relatorios/intersemanal',
    secoes: [
      { slug: 'visao-geral', label: 'Visão Geral' },
      { slug: 'chuva-observada', label: 'Chuva Observada' },
      { slug: 'chuva-prevista', label: 'Chuva Prevista' },
      { slug: 'carga', label: 'Carga' },
      { slug: 'resultados', label: 'Resultados' },
      { slug: 'premissas-gerais', label: 'Premissas Gerais' },
    ],
    variante: { campo: 'cenario', rotulo: 'cenário', labels: DESSEM_CENARIO_LABEL },
    selectDataAnalisada: selectIntersemanalDataAnalisada,
    selectVariante: selectIntersemanalCenario,
    setDataAnalisada: setIntersemanalDataAnalisada,
    habilitado: () => Boolean(featureFlags.relatoriosAprovacao),
  },
  // Semanal e Mensal: chave data (diária, igual ao DatePicker atual) + cenário (D3).
  // A Matriz (`semanal/matriz`) e os Comparadores ficam fora da aprovação (D4/D6).
  semanal: {
    informe: 'semanal',
    titulo: 'Informe Semanal',
    base: '/relatorios/semanal',
    secoes: [
      { slug: 'visao-geral', label: 'Visão Geral' },
      { slug: 'chuva-observada', label: 'Chuva Observada' },
      { slug: 'chuva-prevista', label: 'Chuva Prevista' },
      { slug: 'carga', label: 'Carga' },
      { slug: 'resultados', label: 'Resultados' },
      { slug: 'premissas-gerais', label: 'Premissas Gerais' },
    ],
    variante: { campo: 'cenario', rotulo: 'cenário', labels: DESSEM_CENARIO_LABEL },
    selectDataAnalisada: selectSemanalDataAnalisada,
    selectVariante: selectSemanalCenario,
    setDataAnalisada: setSemanalDataAnalisada,
    habilitado: () => Boolean(featureFlags.relatoriosAprovacao),
  },
  // Capacidade Instalada ainda é placeholder, mas já conta como seção aprovável (D5):
  // evita que dias publicados com 7/7 fiquem incompletos quando a seção ficar pronta.
  mensal: {
    informe: 'mensal',
    titulo: 'Informe Mensal',
    base: '/relatorios/mensal',
    secoes: [
      { slug: 'visao-geral', label: 'Visão Geral' },
      { slug: 'chuva-observada', label: 'Chuva Observada' },
      { slug: 'chuva-prevista', label: 'Chuva Prevista' },
      { slug: 'similaridade-climatica', label: 'Similaridade Climática' },
      { slug: 'carga', label: 'Carga' },
      { slug: 'capacidade-instalada', label: 'Capacidade Instalada' },
      { slug: 'resultados', label: 'Resultados' },
      { slug: 'premissas-gerais', label: 'Premissas Gerais' },
    ],
    variante: { campo: 'cenario', rotulo: 'cenário', labels: DESSEM_CENARIO_LABEL },
    selectDataAnalisada: selectMensalDataAnalisada,
    selectVariante: selectMensalCenario,
    setDataAnalisada: setMensalDataAnalisada,
    habilitado: () => Boolean(featureFlags.relatoriosAprovacao),
  },
};

/** Config do informe; lança se o informe ainda não foi plugado. */
export const getAprovacaoConfig = (informe: RelatorioInforme): RelatorioAprovacaoConfig => {
  const config = RELATORIO_APROVACAO_CONFIG[informe];
  if (!config) throw new Error(`Informe "${informe}" sem configuração de aprovação`);
  return config;
};

/** Todas as configs registradas (usado pelo teste de contrato dos fixtures). */
export const getAprovacaoConfigs = (): RelatorioAprovacaoConfig[] => (
  Object.values(RELATORIO_APROVACAO_CONFIG).filter(
    (config): config is RelatorioAprovacaoConfig => config !== undefined,
  )
);
